/**
 * Admin V3 — Sidebar navigation
 *
 * Grouped (Operations / Financial / Configuration). Modules WITH sub-items are
 * COLLAPSIBLE: the parent acts as an expand/collapse toggle (it does not
 * navigate), sub-items are hidden while collapsed, and the group auto-expands +
 * shows active when the current route is one of its sub-routes. This keeps the
 * sidebar compact. Modules WITHOUT sub-items remain direct NavLinks.
 *
 * Active state matches legacy active-tab styling (bg-slate-900, text-white).
 * All Tailwind class strings come from visualTokens.js. No new palette.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  MODULES, GROUP_ORDER, GROUP_LABEL_KEYS,
} from '../shared/constants/modules';
import {
  cn,
  SIDEBAR_CONTAINER_CLASSES,
  SIDEBAR_GROUP_LABEL_CLASSES,
  SIDEBAR_ITEM_CLASSES,
  SIDEBAR_ITEM_ACTIVE_CLASSES,
  SIDEBAR_SUBITEM_CLASSES,
  SIDEBAR_SUBITEM_ACTIVE_CLASSES,
} from '../shared/visualTokens';
import { t } from '../i18n';
import useCommercialBacklog from '../shared/hooks/useCommercialBacklog';

// A module is "active" when the current path is (or is nested under) one of its
// sub-item routes — used to keep the parent highlighted + auto-expanded.
const moduleHasActiveSubRoute = (module, pathname) =>
  Boolean(module.subItems && module.subItems.some((sub) => {
    const base = `/admin/${sub.route}`;
    return pathname === base || pathname.startsWith(`${base}/`);
  }));

const AdminV3Sidebar = ({ locale = 'fr' }) => {
  const location = useLocation();
  const { pathname } = location;

  // F3 work-queue badge: single source of truth; dormant unless the flag is ON.
  const { enabled: backlogEnabled, summary: backlog } = useCommercialBacklog();

  const byGroup = GROUP_ORDER.reduce((acc, g) => {
    acc[g] = MODULES.filter((m) => m.group === g);
    return acc;
  }, {});

  // Expanded state per collapsible module id. Default: only the module whose
  // sub-route is currently active is expanded (compact by default).
  const computeAutoExpanded = useCallback((prev = {}) => {
    const next = { ...prev };
    MODULES.forEach((m) => {
      if (m.subItems && moduleHasActiveSubRoute(m, pathname)) next[m.id] = true;
    });
    return next;
  }, [pathname]);

  const [expanded, setExpanded] = useState(() => computeAutoExpanded());

  // Keep the active group expanded as the route changes (user can still toggle
  // others open/closed; we never force-collapse a group the user opened).
  useEffect(() => {
    setExpanded((prev) => computeAutoExpanded(prev));
  }, [computeAutoExpanded]);

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <aside className={SIDEBAR_CONTAINER_CLASSES} data-testid="adminv3-sidebar">
      <nav className="py-2">
        {GROUP_ORDER.map((group) => (
          <div key={group}>
            <div className={SIDEBAR_GROUP_LABEL_CLASSES}>
              {t(GROUP_LABEL_KEYS[group], locale)}
            </div>
            {byGroup[group].map((module) => {
              // Leaf module → direct NavLink (unchanged behavior).
              if (!module.subItems) {
                return (
                  <NavLink
                    key={module.id}
                    to={`/admin/${module.route}`}
                    end
                    className={({ isActive }) =>
                      cn(SIDEBAR_ITEM_CLASSES, isActive && SIDEBAR_ITEM_ACTIVE_CLASSES)
                    }
                    data-testid={`adminv3-nav-${module.id}`}
                  >
                    {module.icon && <module.icon className="h-4 w-4 flex-shrink-0" />}
                    <span className="truncate">{t(`nav.${module.labelKey}`, locale)}</span>
                  </NavLink>
                );
              }

              // Collapsible parent → toggle button (does not navigate).
              const isOpen = Boolean(expanded[module.id]);
              const isActive = moduleHasActiveSubRoute(module, pathname);
              return (
                <React.Fragment key={module.id}>
                  <button
                    type="button"
                    onClick={() => toggle(module.id)}
                    aria-expanded={isOpen}
                    className={cn(SIDEBAR_ITEM_CLASSES, 'w-full justify-between', isActive && SIDEBAR_ITEM_ACTIVE_CLASSES)}
                    data-testid={`adminv3-nav-${module.id}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      {module.icon && <module.icon className="h-4 w-4 flex-shrink-0" />}
                      <span className="truncate">{t(`nav.${module.labelKey}`, locale)}</span>
                    </span>
                    {isOpen
                      ? <ChevronDown className="h-4 w-4 flex-shrink-0" />
                      : <ChevronRight className="h-4 w-4 flex-shrink-0" />}
                  </button>

                  {isOpen && module.subItems.map((sub) => {
                    const badge = (backlogEnabled && sub.badgeKey)
                      ? Number(backlog?.[sub.badgeKey]) || 0 : 0;
                    return (
                      <NavLink
                        key={sub.id}
                        to={`/admin/${sub.route}`}
                        end
                        className={({ isActive: subActive }) =>
                          cn(SIDEBAR_SUBITEM_CLASSES, subActive && SIDEBAR_SUBITEM_ACTIVE_CLASSES)
                        }
                        data-testid={`adminv3-nav-${sub.id}`}
                      >
                        <span className="truncate">{t(`nav.${sub.labelKey}`, locale)}</span>
                        {badge > 0 && (
                          <span
                            className="ml-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white"
                            data-testid={`adminv3-nav-badge-${sub.id}`}
                          >
                            {badge}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        ))}
      </nav>
    </aside>
  );
};

export default AdminV3Sidebar;
